import { useQuery } from '@tanstack/react-query';
import { fetchTeamStats, fetchMyCases, fetchMyConversations } from '../services/homeStats';
import type { ConversationRow } from '../services/conversations';
import { HOME_MY_CASES_KEY, HOME_MY_CONVERSATIONS_KEY, HOME_TEAM_STATS_KEY } from './queryKeys';
import { rangeBounds, type HomeRangeKey } from './useBillingRange';

// The team-stats response is served from a 10-minute server-side cache (the
// BigQuery half of it is billed per byte scanned), so a short client staleTime
// would only produce round trips that return the same bytes. Five minutes here
// keeps the client roughly in step with the server's cache without ever being
// the thing that triggers a fresh scan.
const TEAM_STATS_STALE_MS = 5 * 60 * 1000;

// My queue changes whenever anyone assigns or re-prioritises, so it (and the
// conversations card below) refreshes on the Work Queue's own cadence.
const MY_CASES_STALE_MS = 60 * 1000;
const MY_CASES_REFETCH_MS = 2 * 60 * 1000;

function isoDay(date: Date): string {
    return date.toISOString().slice(0, 10);
}

export function useTeamStats(range: HomeRangeKey) {
    const { from, to } = rangeBounds(range);
    const query = useQuery({
        queryKey: HOME_TEAM_STATS_KEY(range, from ? isoDay(from) : null, isoDay(to)),
        queryFn: () => fetchTeamStats(from?.toISOString() ?? null, to.toISOString()),
        staleTime: TEAM_STATS_STALE_MS,
        // Keep the previous range's numbers on screen while the next range
        // loads, so switching 30D → YTD dims rather than blanking the page.
        placeholderData: (previous) => previous
    });

    return {
        stats: query.data ?? null,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        refetch: query.refetch
    };
}

// Rows shown by each of Home's two "mine" cards.
export const MY_LIST_LIMIT = 8;

export function useMyAssignedCases(limit: number) {
    const query = useQuery({
        queryKey: HOME_MY_CASES_KEY(limit),
        queryFn: () => fetchMyCases(limit),
        staleTime: MY_CASES_STALE_MS,
        refetchInterval: MY_CASES_REFETCH_MS,
        refetchOnWindowFocus: true
    });

    return {
        myCases: query.data ?? null,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch
    };
}

export interface MyUnreadConversations {
    conversations: ConversationRow[];
    isLoading: boolean;
    isError: boolean;
}

// My unread conversations, topped up with unclaimed unread on my team. The
// server resolves both halves from the caller, so this needs no user context.
export function useMyUnreadConversations(limit: number): MyUnreadConversations {
    const query = useQuery({
        queryKey: HOME_MY_CONVERSATIONS_KEY(limit),
        queryFn: () => fetchMyConversations(limit),
        staleTime: MY_CASES_STALE_MS,
        refetchInterval: MY_CASES_REFETCH_MS,
        refetchOnWindowFocus: 'always'
    });

    return {
        conversations: query.data?.conversations ?? [],
        isLoading: query.isLoading,
        isError: query.isError
    };
}
